import { z } from "zod";

import { IncompleteDocumentType } from "@/src/models/constants/document-types";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import { DocumentDateSchema } from "@/src/models/documents/document-dates";
import { ExtractedDocumentFields } from "@/src/models/documents/extracted-document-fields";
import { patientMinimalSchema } from "@/src/models/patient";

export const incompleteDisplayType: Record<IncompleteDocumentType, string> = {
  [IncompleteDocumentType.ILLEGIBLE]: "Illegible Fax",
  [IncompleteDocumentType.NOT_ALL_PAGES]: "Incomplete Fax",
  [IncompleteDocumentType.CANNOT_LINK_TO_PATIENT]: "Missing PHI - cannot link to patient",
  [IncompleteDocumentType.INCOMPLETE]: "Incomplete",
};

const baseIncompleteFields = {
  patient: patientMinimalSchema.nullable(),
};

const extendedIncompleteSchema = z.object({
  ...DocumentDateSchema.shape,
});

const incompleteCustomFields: Record<IncompleteDocumentType, Record<string, z.ZodTypeAny>> = {
  [IncompleteDocumentType.CANNOT_LINK_TO_PATIENT]: extendedIncompleteSchema.pick({ dateReceived: true }).shape,
  [IncompleteDocumentType.ILLEGIBLE]: extendedIncompleteSchema.pick({ dateReceived: true }).shape,
  [IncompleteDocumentType.NOT_ALL_PAGES]: extendedIncompleteSchema.pick({ dateReceived: true }).shape,
  [IncompleteDocumentType.INCOMPLETE]: extendedIncompleteSchema.shape,
};

const makeIncompleteSchema = (type: IncompleteDocumentType, customFields: Record<string, z.ZodTypeAny>) =>
  z.object({
    type: z.literal(type),
    ...baseIncompleteFields,
    ...customFields,
  });

export const incompleteSchemas: Record<IncompleteDocumentType, ReturnType<typeof makeIncompleteSchema>> = (
  Object.values(IncompleteDocumentType) as IncompleteDocumentType[]
).reduce(
  (acc, type) => {
    acc[type] = makeIncompleteSchema(type, incompleteCustomFields[type]);

    return acc;
  },
  {} as Record<IncompleteDocumentType, ReturnType<typeof makeIncompleteSchema>>
);

const incompleteSchemasArray = (Object.values(IncompleteDocumentType) as IncompleteDocumentType[]).map(
  (type) => incompleteSchemas[type]
) as [ReturnType<typeof makeIncompleteSchema>, ...ReturnType<typeof makeIncompleteSchema>[]];

export const anyIncompleteSchema = z.discriminatedUnion("type", incompleteSchemasArray);

/** See {@link ExtractedDocumentFields} for why the fields are reconstructed rather than inferred. */
export type Incomplete = ExtractedDocumentFields<
  IncompleteDocumentType,
  typeof extendedIncompleteSchema,
  typeof baseIncompleteFields
> &
  DatabaseDefaults;

const now = new Date().toISOString();

export const incompleteShape: Incomplete = {
  id: "",
  type: IncompleteDocumentType.INCOMPLETE,
  patient: null,
  dateReceived: null,
  dateOfDocument: null,
  createdAt: now,
  updatedAt: now,
  deletedAt: null,
  groupId: "",
};
